import React, { useState, useEffect } from "react";
import ModalComp from "../themes/Model";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import api from "../axiosConfig";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  createTheme,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Snackbar,
  TextField,
  ThemeProvider,
} from "@mui/material";
import { getUser } from "../helpers";
import { LoadingButton } from "@mui/lab";

const SchemeModalWrapper = ({
  open,
  mode,
  handleClose,
  data,
  setCustomOpenModal,
}) => {
  const [formData, setFormData] = useState({
    warehouse: "",
    schemeStartDate: null,
    schemeEndDate: null,
    channel: "",
    pricelist: "",
    schemeName: "",
    product: "",
    schemeType: "",
    rateImpact: "",
    quantityImpact: "",
    schemeImpactValue: "",
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [adjustWithExistingQty, setAdjustWithExistingQty] = useState(0);

  const user = getUser();

  useEffect(() => {
    if (data) {
      setFormData({
        ...formData,
        warehouse: data?.data?.Warehouse,
        schemeStartDate: data?.data?.SchemeStartDate
          ? dayjs(data.SchemeStartDate)
          : null,
        schemeEndDate: data?.data?.SchemeEndDate
          ? dayjs(data.SchemeEndDate)
          : null,
        channel: data?.data?.Channel,
        pricelist: data?.data?.Pricelist,
        schemeName: data?.data?.Scheme_Name,
        product: data?.data?.ProductCode,
        productName: data?.data?.Product_Name,
        schemeType: data?.data?.Scheme_Type,
        rateImpact: Math.round(data?.data?.Rate_Impact * 100),
        quantityImpact: Math.round(data?.data?.Qty_Impact * 100),
      });
    }
  }, [data]);
  // dropdown data
  const mrpRate = [
    { MrpValue: "MRP", name: "MRP" },
    { MrpValue: "PTR", name: "PTR" },
  ];
  const isDateValid =
    formData.schemeStartDate &&
    formData.schemeEndDate &&
    dayjs(formData.schemeEndDate).isAfter(formData.schemeStartDate) &&
    formData.schemeName &&
    formData.mrpRate;
  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleCheckboxChange = (event) => {
    setAdjustWithExistingQty(event.target.checked ? 1 : 0);
  };

  const handleSave = async () => {
    const newFormData = {
      ...formData,
      schemeStartDate: dayjs(formData.schemeStartDate).format("YYYY-MM-DD"),
      schemeEndDate: dayjs(formData.schemeEndDate).format("YYYY-MM-DD"),
    };

    setLoading(true);

    try {
      const response = await api.post("/api/scheme/addnew", {
        body: {
          ...newFormData,
          user,
          page: "scheme",
          quantityImpact: newFormData.quantityImpact,
          rateImpact: newFormData.rateImpact,
          adjustWithExistingQty: adjustWithExistingQty || 0,
        },
      });

      if (response.data.Status === false) {
        setSnackbarMessage(response.data.SchemeStatus);
        setSnackbarOpen(true);
        setLoading(false);
      } else {
        setSnackbarMessage("Scheme added successfully!");
        setSnackbarOpen(true);
        handleClose();
        setLoading(false);
      }
    } catch (error) {
      console.error("Error submitting scheme data:", error);
      setSnackbarMessage("Failed to submit scheme data.");
      setSnackbarOpen(true);
      setLoading(false);
    }
  };

  const isRepeatMode = mode === "repeat";
  const isEditMode = mode === "edit";
  const modalTitle =
    mode === "repeat"
      ? "Repeat Scheme"
      : mode === "edit"
      ? "Modify/Edit Scheme"
      : mode === "approve"
      ? "Approve Request"
      : "Reject Request";

  const confirmationContent = {
    approve: {
      title: "Approve Request",
      message: "Are you sure you want to approve this request?",
      confirmButtonText: "Yes",
    },
    reject: {
      title: "Reject Request",
      message: "Are you sure you want to reject this request?",
      confirmButtonText: "Yes",
    },
  };

  // date picker style

  const customTheme = createTheme({
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiInputBase-input": {
              fontSize: "14px", // Font size for the input field
            },
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            fontSize: "12px", // Font size for the calendar days
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            fontSize: "12px", // Font size for typography inside the Date Picker
          },
        },
      },
    },
  });

  const formContent = (
    <div>
      <TextField
        label="Warehouse"
        value={formData.warehouse || ""}
        disabled={isRepeatMode || isEditMode}
        onChange={(e) =>
          setFormData({ ...formData, warehouse: e.target.value })
        }
        fullWidth
        sx={{ marginBottom: "15px" }}
        size="small"
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: "20px",
          marginBottom: "15px",
        }}
      >
        <ThemeProvider theme={customTheme}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              className="scheme-date-picker"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: "20px",
                width: "100%",
              }}
            >
              <DatePicker
                label="Scheme Start Date"
                sx={{ width: "50%" }}
                value={formData.schemeStartDate || null}
                onChange={(newValue) =>
                  setFormData({ ...formData, schemeStartDate: newValue })
                }
                renderInput={(params) => (
                  <TextField {...params} fullWidth margin="normal" />
                )}
                disablePast={true}
                shouldDisableDate={(date) => {
                  const today = dayjs().startOf("day");
                  const tomorrow = today.add(1, "day");
                  return (
                    date.isSame(today, "day") || date.isSame(tomorrow, "day")
                  );
                }}
              />
              <DatePicker
                label="Scheme End Date"
                sx={{ width: "50%" }}
                value={formData.schemeEndDate || null}
                onChange={(newValue) =>
                  setFormData({ ...formData, schemeEndDate: newValue })
                }
                renderInput={(params) => (
                  <TextField {...params} fullWidth margin="normal" />
                )}
                disablePast={true}
                shouldDisableDate={(date) => {
                  const today = dayjs().startOf("day");
                  const schemeStartDate = formData.schemeStartDate
                    ? dayjs(formData.schemeStartDate)
                    : null;
                  return (
                    date.isBefore(today) ||
                    (schemeStartDate && date.isBefore(schemeStartDate))
                  );
                }}
              />
            </Box>
          </LocalizationProvider>
        </ThemeProvider>
      </Box>

      <Grid
        item
        sx={{
          display: "flex",
          gap: "20px",
          width: "100%",
        }}
      >
        <TextField
          label="Channel"
          value={formData.channel || ""}
          disabled={isRepeatMode || isEditMode}
          onChange={(e) =>
            setFormData({ ...formData, Channel: e.target.value })
          }
          fullWidth
          sx={{ marginBottom: "15px" }}
          size="small"
        />

        <TextField
          label="Pricelist"
          value={formData.pricelist || ""}
          disabled={isRepeatMode || isEditMode}
          onChange={(e) =>
            setFormData({ ...formData, Pricelist: e.target.value })
          }
          fullWidth
          sx={{ marginBottom: "15px" }}
          size="small"
        />
      </Grid>
      <Grid
        item
        sx={{
          display: "flex",
          gap: "20px",
          width: "100%",
        }}
      >
        <TextField
          label="Scheme Name"
          value={formData.schemeName || ""}
          disabled={isRepeatMode}
          onChange={(e) =>
            setFormData({ ...formData, schemeName: e.target.value })
          }
          fullWidth
          sx={{ marginBottom: "15px" }}
          size="small"
        />

        <TextField
          label="Product name"
          value={formData.productName || ""}
          disabled={isRepeatMode || isEditMode}
          onChange={(e) =>
            setFormData({ ...formData, productName: e.target.value })
          }
          fullWidth
          sx={{ marginBottom: "15px" }}
          size="small"
        />
      </Grid>

      <TextField
        label="Scheme Type"
        value={formData.schemeType || ""}
        disabled={isRepeatMode || isEditMode}
        onChange={(e) =>
          setFormData({ ...formData, Scheme_Type: e.target.value })
        }
        fullWidth
        sx={{ marginBottom: "15px" }}
        size="small"
      />
      {["OnInv", "OffInv"].includes(formData.schemeType) && (
        <Grid
          item
          sx={{
            display: "flex",
            gap: "20px",
            width: "100%",
          }}
        >
          <TextField
            fullWidth
            select
            label="Discount Pricing Impact"
            name="mrpRate"
            value={formData.mrpRate || ""}
            sx={{ marginBottom: "15px" }}
            onChange={(e) =>
              setFormData({ ...formData, mrpRate: e.target.value })
            }
            size="small"
          >
            {mrpRate.map((rate, index) => (
              <MenuItem key={index} value={rate.MrpValue}>
                {rate.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      )}
      <Grid
        item
        sx={{
          display: "flex",
          gap: "20px",
          width: "100%",
        }}
      >
        <TextField
          label="Scheme Rate Impact %"
          value={formData.rateImpact}
          disabled={isRepeatMode}
          onChange={(e) => {
            const value = e.target.value.replace("%", "");
            setFormData({
              ...formData,
              rateImpact: value,
            });
          }}
          fullWidth
          sx={{ marginBottom: "15px" }}
          InputProps={{
            endAdornment:
              formData.rateImpact !== undefined &&
              formData.rateImpact !== "" ? (
                <span style={{ marginLeft: "-3px", marginRight: "3px" }}>
                  %
                </span>
              ) : null,
          }}
          size="small"
        />

        <TextField
          label="Scheme Quantity Impact %"
          value={formData.quantityImpact}
          disabled={isRepeatMode}
          onChange={(e) => {
            const value = e.target.value.replace("%", "");
            setFormData({
              ...formData,
              quantityImpact: value,
            });
          }}
          fullWidth
          sx={{ marginBottom: "15px" }}
          InputProps={{
            endAdornment:
              formData.quantityImpact !== undefined &&
              formData.quantityImpact !== "" ? (
                <span style={{ marginLeft: "-3px", marginRight: "3px" }}>
                  %
                </span>
              ) : null,
          }}
          size="small"
        />
      </Grid>

      {["OnInv", "OffInv"].includes(formData.schemeType) && (
        <Grid
          item
          sx={{
            display: "flex",
            gap: "20px",
            width: "100%",
          }}
        >
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={adjustWithExistingQty === 1}
                  onChange={handleCheckboxChange}
                  sx={{
                    color: "#149485",
                    "&.Mui-checked": {
                      color: "#149485",
                      width: "fit-content",
                    },
                  }}
                />
              }
              label="Adjust with existing qty"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px", // Set font size for the label
                },
              }}
            />
          </FormGroup>
        </Grid>
      )}
    </div>
  );

  const actionButtons = (
    <>
      {(isRepeatMode || isEditMode) && (
        <LoadingButton
          variant="contained"
          color="primary"
          loading={loading}
          loadingPosition="end"
          onClick={handleSave}
          style={{
            fontSize: "12px",
            backgroundColor: loading ? "#16a69590" : "#16a695",
            color: "white",
            opacity: !isDateValid ? 0.5 : 1,
            cursor: !isDateValid ? "not-allowed" : "pointer",
            paddingRight: loading ? "35px" : "",
          }}
          disabled={!isDateValid}
          size="small"
        >
          {isRepeatMode
            ? loading
              ? "Updating Repeat Request..."
              : "Request Repeat"
            : loading
            ? "Updating Modify..."
            : "Request Modify"}
        </LoadingButton>
      )}
      {mode === "approve" && (
        <Button
          variant="contained"
          color="primary"
          onClick={handleClose}
          style={{
            fontSize: "12px",
            backgroundColor: "#16a695",
            color: "white",
            marginRight: "8px",
          }}
          size="small"
        >
          Yes
        </Button>
      )}
      {mode === "reject" && (
        <Button
          variant="contained"
          color="primary"
          onClick={handleClose}
          style={{
            fontSize: "12px",
            backgroundColor: "#16a695",
            color: "white",
            marginRight: "8px",
          }}
          size="small"
        >
          Yes
        </Button>
      )}
      <Button
        variant="outlined"
        onClick={handleClose}
        style={{ fontSize: "12px", color: "#000", borderColor: "#707070" }}
        size="small"
      >
        Cancel
      </Button>
    </>
  );

  return (
    <>
      <ModalComp
        open={open}
        setCustomOpenModal={setCustomOpenModal}
        handleClose={handleClose}
        title={modalTitle}
        modalContent={
          mode === "approve" || mode === "reject"
            ? confirmationContent[mode].message
            : formContent
        }
        modelButtons={actionButtons}
        onSave={handleSave}
        datePickerStyle={{ width: "48%", margin: "5px" }}
        themeStyles={{
          boxStyle: {
            width: mode === "approve" || mode === "reject" ? "682px" : "682px",
            // height:
            //   mode === "approve" || mode === "reject" ? "fit-content" : "565px",
            height: "auto",
            margin: "auto",
            marginTop: "80px",
          },
          titleStyle: {
            fontSize: "18px",
            fontWeight: 600,
            color: "#000",
            backgroundColor: "#F4F4F4",
            padding: "24px",
            position: "sticky",
            top: 0,
            zIndex: 10,
          },
          actionButtonContainerStyle: {
            padding: "0 20px 20px 20px",
          },
          fieldStyle: { width: "100%", marginTop: "20px" },
        }}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={
            snackbarMessage.includes("successfully") ? "success" : "error"
          } // Set severity dynamically
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SchemeModalWrapper;
